import { ChangeEvent, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Interpolation, Theme } from '@emotion/react';
import { TemplateType_Slug } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { Vertical } from '@weave/schema-gen-ts/dist/shared/vertical/vertical.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { useQueryClient } from 'react-query';
import { ReviewsInvitationV1 } from '@frontend/api-reviews-invitation';
import { TemplatorV2Mutations, TemplatorV2Queries } from '@frontend/api-templator-v2';
import { NoAccessEmptyState } from '@frontend/assets';
import { getUser, hasSchemaACL } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { useLastUsedVerticalShallowStore } from '@frontend/location-helpers';
import {
  useReviewLandingPageStore,
  useTemplateFormShallowStore,
  useTemplateFormStore,
} from '@frontend/reviews-landing-template';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { TemplatesPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import {
  useForm,
  TagType,
  useTooltip,
  useAlert,
  SpinningLoader,
  Stepper,
  StepStatus,
  Button,
  Modal,
  MessageTemplate,
} from '@frontend/design-system';
import { useAvailableTemplateTypesQuery, useStepperFormActions, StepsValidationData } from '../hooks';
import { LEGACY_REVIEW_PAGE_ID } from './constants';
import {
  TemplateTileStep,
  TemplateTileStepCollapsed,
  LocationStep,
  LocationStepCollapsed,
  TemplateTypeStep,
  TemplateTypeStepCollapsed,
  TemplateMessageStep,
  TemplateMessageStepCollapsed,
  TemplateTypeNonStepper,
  ReviewLinkStep,
  TemplateTitleNonStepper,
} from './template-steps';

type Props = {
  mode: 'create' | 'edit' | 'inbox-edit';
  closeSettingsOnSave?: boolean;
  closeSettingsOnCancel?: boolean;
  templateFormVariant?: 'guided' | 'quick';
  formCompletionHandler?: () => void;
  currentTab?: string;
};

export type StepsRenderData = {
  Component: ReactElement;
  collapsedValue: ReactElement;
}[];

/**
 * To create or edit template form
 *
 * This component uses
 *  - react-query to handle server side state. (in edit mode only)
 *  - zustand store to persist local form edits across navigation between this component and review-landing-page-editor.
 *  - useForm to handle form validations and submissions.
 *
 */
export const TemplateForm = ({
  mode,
  closeSettingsOnSave,
  closeSettingsOnCancel,
  templateFormVariant,
  formCompletionHandler,
  currentTab,
}: Props) => {
  const { setTemplateFormStateInStore, ...templateFormStoreState } = useTemplateFormShallowStore(
    'setTemplateFormStateInStore',
    'activeStep',
    'locations',
    'name',
    'templateId',
    'templateString',
    'templateType'
  );
  const quickTemplateFormVariant = templateFormVariant === 'quick';
  const { reset: resetTemplateFormStore } = useTemplateFormStore;
  const { reviewLandingPageId: reviewLandingPageIdFromStore } = useReviewLandingPageStore();
  const { lastUsedVertical } = useLastUsedVerticalShallowStore('lastUsedVertical');
  const isVet = lastUsedVertical === Vertical.VET;
  const { getFeatureFlagValue } = useScopedAppFlagStore();

  const { t } = useTranslation('templates-settings');
  const { navigate, closeSettings } = useSettingsNavigate();
  const { selectedOrgId, selectedLocationIds } = useAppScopeStore();
  const aclEnforcementEnabled = getFeatureFlagValue('nwx:messaging-inbox-fe-acl-enforcement', 'ALL');
  const locationIdsWithACL = aclEnforcementEnabled
    ? selectedLocationIds.filter((locationId) => hasSchemaACL(locationId, Permission.MANUAL_MESSAGE_TEMPLATES_MANAGE))
    : selectedLocationIds;
  const { Tooltip, tooltipProps, triggerProps } = useTooltip({
    placement: 'top',
  });
  const [initialTemplate, setInitialTemplate] = useState<string>(templateFormStoreState.templateString ?? '');

  const createModeLocations =
    locationIdsWithACL.length === 1
      ? locationIdsWithACL
      : templateFormStoreState.locations?.filter((locationId) => locationIdsWithACL.includes(locationId)) ?? [];

  useEffect(() => {
    if (locationIdsWithACL.length !== 1 && createModeLocations.length !== templateFormStoreState.locations?.length) {
      setTemplateFormStateInStore({
        ...templateFormStoreState,
        locations: createModeLocations,
      });
    }
  }, [JSON.stringify(createModeLocations)]);

  const user = getUser();
  const alert = useAlert();
  const queryClient = useQueryClient();
  const successMessage = mode === 'create' ? t('Template created successfully') : t('Template updated successfully');
  const errorMessage = mode === 'create' ? t('Failed to create template') : t('Failed to update template');

  const getNameLabel = () => {
    switch (mode) {
      case 'create':
        return t('New Template Title');
      case 'edit':
        return t('Template Title');
      default:
        return t('Template Title');
    }
  };

  // Form validation + submission logic
  const {
    getFieldProps,
    formProps,
    seedValues,
    values: templateFormValues,
  } = useForm({
    allowInvalidSubmission: false,
    fields: {
      name: {
        type: 'text',
        required: true,
        placeholder: getNameLabel(),
        value: templateFormStoreState.name ?? '',
        validateOnChange: true,
      },
      locationIds: {
        type: 'checklist',
        required: true,
        value: mode === 'create' ? createModeLocations : templateFormStoreState.locations ?? [],
        validateOnChange: true,
      },
      type: {
        type: 'dropdown',
        required: true,
        value: templateFormStoreState.templateType ?? currentTab ?? '', // currentTab is used for default value if it comes from props
        validateOnChange: true,
      },
      message: {
        type: 'text',
        required: true,
        value: templateFormStoreState.templateString ?? '',
        validateOnChange: true,
      },
      reviewLink: {
        type: 'dropdown',
        required:
          quickTemplateFormVariant &&
          (templateFormStoreState.templateType === TemplateType_Slug.REVIEW_REQUEST ||
            templateFormStoreState.templateType === TemplateType_Slug.REVIEW_REQUEST_PET),
        value: undefined,
        validateOnChange: true,
      },
    },
    onSubmit: async (values) => {
      if (isMissingRequiredTag) return;
      if (values?.message) {
        const req = {
          orgId: selectedOrgId,
          templateTypeSlug: values.type as TemplateType_Slug,
          name: values.name ?? '',
          businessGroupIds: values.locationIds ?? [],
          templateString: values.message ?? '',
          userId: user?.userID ?? '',
        };

        if (
          isTemplateTypeReview &&
          reviewLandingPageEditorFeatureFlag &&
          reviewLandingPageIdFromStore !== LEGACY_REVIEW_PAGE_ID
        )
          upsertReviewInvitationMutation();
        else if (mode === 'create') {
          createTemplateMutation.mutateAsync({
            ...req,
            locale: 'en_US',
          });
        } else {
          // Currently, the BE doesn't allow updating the template type, so we need to delete the template and create a new one.
          const templateTypeHasChanged = templateFormStoreState.templateType !== values.type;
          if (templateTypeHasChanged && templateFormStoreState.templateId) {
            deleteTemplateMutation.mutateAsync({
              templateId: templateFormStoreState.templateId ?? '',
              templateTypeSlug: templateFormStoreState.templateType ?? ('' as TemplateType_Slug),
              orgId: selectedOrgId,
              userId: user?.userID ?? '',
            });
            createTemplateMutation.mutateAsync({
              ...req,
              locale: 'en_US',
            });
            return;
          }
          updateTemplateMutation.mutateAsync({
            ...req,
            templateId: templateFormStoreState.templateId ?? '',
          });
        }
      }
    },
  });
  const { onChange: messageTemplateOnChange, ...messageFieldProps } = getFieldProps('message');
  const nameFieldProps = getFieldProps('name');
  const locationsFieldProps = getFieldProps('locationIds');
  const typeFieldProps = getFieldProps('type');
  const reviewLinkFieldProps = getFieldProps('reviewLink');
  const handleMessageChange = (message: string) => {
    const e = { target: { name: 'message', value: message } } as ChangeEvent<HTMLInputElement>;
    messageTemplateOnChange(e);
  };

  const isFormModeEdit = mode === 'edit' || !!templateFormStoreState.templateId;
  const isTemplateTypeReview = [
    TemplateType_Slug.REVIEW_REQUEST,
    TemplateType_Slug.REVIEW_REQUEST_PET,
    TemplateType_Slug.MANUAL_REVIEW_REQUEST,
    TemplateType_Slug.MANUAL_REVIEW_REQUEST_PET,
  ].includes(typeFieldProps.value as TemplateType_Slug);

  // Template Mutations
  const onSuccess = () => {
    alert.success(successMessage);
    resetTemplateFormStore();
    if (closeSettingsOnSave) {
      closeSettings();
      return;
    }
    if (quickTemplateFormVariant) {
      queryClient.invalidateQueries({
        queryKey: ['templator-v2', selectedOrgId, 'listTemplates'],
      });
      formCompletionHandler?.();
      return;
    }
    navigate({ to: '/messaging/templates' });
  };
  const onError = () => {
    alert.error(errorMessage);
  };
  const createTemplateMutation = TemplatorV2Mutations.useCreateTemplateMutation({
    onSuccess,
    onError,
  });
  const updateTemplateMutation = TemplatorV2Mutations.useUpdateTemplateMutation({
    onSuccess,
    onError,
  });
  const deleteTemplateMutation = TemplatorV2Mutations.useDeleteTemplateMutation();

  // Reviews Invitation template mutations
  const createReviewInvitationTemplate = ReviewsInvitationV1.Mutations.useCreateInvitationTemplateMutation({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries(TemplatorV2Queries.queryKeys.listTemplatesQuery({ orgId: selectedOrgId }));
        onSuccess();
      },
      onError,
    },
  });
  const updateReviewInvitationTemplate = ReviewsInvitationV1.Mutations.useUpdateInvitationTemplateMutation({
    options: {
      onSuccess: (_, request) => {
        queryClient.invalidateQueries(TemplatorV2Queries.queryKeys.listTemplatesQuery({ orgId: selectedOrgId }));
        queryClient.invalidateQueries(
          TemplatorV2Queries.queryKeys.getTemplateQuery({
            orgId: selectedOrgId,
            templateId: request.templateId,
          })
        );
        onSuccess();
      },
      onError,
    },
  });
  const deleteReviewInvitationTemplate = ReviewsInvitationV1.Mutations.useDeleteInvitationTemplateMutation({
    options: {
      onSuccess: (_, request) => {
        queryClient.invalidateQueries(TemplatorV2Queries.queryKeys.listTemplatesQuery({ orgId: selectedOrgId }));
        queryClient.invalidateQueries(
          TemplatorV2Queries.queryKeys.getTemplateQuery({
            orgId: selectedOrgId,
            templateId: request.templateId,
          })
        );
      },
    },
  });

  const upsertReviewInvitationMutation = useCallback(async () => {
    const reviewSite = quickTemplateFormVariant
      ? {
          name: templateFormValues.reviewLink ?? '',
          templateId: templateFormValues.reviewLink ?? '',
        }
      : {
          name: reviewLandingPageIdFromStore,
          templateId: reviewLandingPageIdFromStore,
        };
    const upsertReviewInvitationTemplateData = {
      templateId: templateFormStoreState.templateId ?? '', // TODO: Maybe ask BE to remove this for createInvitationTemplate
      groupIds: templateFormValues.locationIds ?? locationIdsWithACL,
      title: templateFormValues.name ?? '',
      messageTemplate: templateFormValues.message ?? '',
      reviewSite: reviewSite,
    };
    if (!isFormModeEdit) {
      createReviewInvitationTemplate.mutateAsync({ template: upsertReviewInvitationTemplateData, isPet: isVet });
    } else if (
      isVet &&
      templateFormStoreState.templateType &&
      templateFormStoreState.templateId &&
      [TemplateType_Slug.REVIEW_REQUEST, TemplateType_Slug.MANUAL_REVIEW_REQUEST].includes(
        templateFormStoreState.templateType
      )
    ) {
      // Template type is immutable, so in order to migrate from non-pet to pet, we must delete and then create
      await deleteReviewInvitationTemplate.mutateAsync({
        templateId: templateFormStoreState.templateId,
        isPet: false,
      });
      await createReviewInvitationTemplate.mutateAsync({ template: upsertReviewInvitationTemplateData, isPet: true });
    } else {
      updateReviewInvitationTemplate.mutateAsync({ ...upsertReviewInvitationTemplateData, isPet: isVet });
    }
  }, [
    isFormModeEdit,
    templateFormValues,
    templateFormStoreState,
    locationIdsWithACL,
    createReviewInvitationTemplate,
    updateReviewInvitationTemplate,
    isVet,
  ]);

  const { data: templateTypes = [], isLoading: isLoadingTemplateTypes } = useAvailableTemplateTypesQuery(
    locationsFieldProps.value?.length ? locationsFieldProps.value : undefined
  );
  // Filter the review template types based on the vertical of the selected locations
  const availableTemplateTypes = templateTypes?.map((templateType) => templateType.slug) ?? [];
  const defaultTemplateTags =
    templateTypes.find((templateType) => templateType.slug === TemplateType_Slug.MANUAL_MESSAGE)?.dynamicFields ?? [];
  const templateTags = useMemo(
    () =>
      templateTypes.find((templateType) => templateType.slug === typeFieldProps.value)?.dynamicFields ??
      defaultTemplateTags,
    [templateTypes, typeFieldProps.value, defaultTemplateTags]
  );
  const requiredTags = templateTags.filter((tag) => tag.isRequired);
  const hasRequiredTag = requiredTags.length > 0;
  const isMissingRequiredTag =
    hasRequiredTag && !requiredTags.every((tag) => messageFieldProps.value?.includes(tag.key!));

  useEffect(() => {
    // If the reviews template type changes from pet to general or vice versa, we need to update the selected type to the new type.
    if (
      typeFieldProps.value === TemplateType_Slug.MANUAL_MESSAGE &&
      availableTemplateTypes.includes(TemplateType_Slug.MANUAL_MESSAGE_PET) &&
      !availableTemplateTypes.includes(TemplateType_Slug.MANUAL_MESSAGE)
    ) {
      seedValues({
        type: TemplateType_Slug.MANUAL_MESSAGE_PET,
      });
    } else if (
      typeFieldProps.value === TemplateType_Slug.MANUAL_REVIEW_REQUEST_PET &&
      availableTemplateTypes.includes(TemplateType_Slug.MANUAL_REVIEW_REQUEST) &&
      !availableTemplateTypes.includes(TemplateType_Slug.MANUAL_REVIEW_REQUEST_PET)
    ) {
      seedValues({
        type: TemplateType_Slug.MANUAL_REVIEW_REQUEST,
      });
    } else if (
      typeFieldProps.value === TemplateType_Slug.MANUAL_REVIEW_REQUEST &&
      availableTemplateTypes.includes(TemplateType_Slug.MANUAL_REVIEW_REQUEST_PET) &&
      !availableTemplateTypes.includes(TemplateType_Slug.MANUAL_REVIEW_REQUEST)
    ) {
      seedValues({
        type: TemplateType_Slug.MANUAL_REVIEW_REQUEST_PET,
      });
    } else if (typeFieldProps.value && !availableTemplateTypes.includes(typeFieldProps.value as TemplateType_Slug)) {
      seedValues({
        type: templateFormStoreState.templateType ?? '',
      });
    }
  }, [availableTemplateTypes]);

  const formatKey = (key: string) => '{{' + key + '}}';

  const mappedTags = useMemo(
    () =>
      templateTags.map((tag) => {
        const transformedValue = tag.transformations?.find(({ arg }) => !!arg)?.arg;
        const formattedTag: TagType = {
          label: tag.label ?? '',
          value: (transformedValue || tag.exampleValues?.join(' ')) ?? '',
          key: tag.key ? formatKey(tag.key) : '',
          optional: tag.isRequired ? false : undefined,
        };
        return formattedTag;
      }),
    [templateTags]
  );

  const helperTexts = useMemo<{ dynamicTagMessage?: string; requiredTagMessage?: string }>(() => {
    const texts: { dynamicTagMessage?: string; requiredTagMessage?: string } = {};
    texts.dynamicTagMessage = t(
      'You can drag or click the dynamic fields below to tailor your message to your recipient'
    );
    if (hasRequiredTag) texts.requiredTagMessage = t('Required field *');
    return texts;
  }, [hasRequiredTag]);

  // Stepper logic
  // uses 1-indexing for stepStatus object
  const canShowLocationStep = locationIdsWithACL.length > 1;
  const maxStep = canShowLocationStep ? 4 : 3; // maxStep starts with 1 index.
  const {
    activeStep,
    stepStatus,
    updateAllStepperStatus,
    updateStepperStatusByStepNumber,
    next,
    previous,
    finish,
    skipTo,
  } = useStepperFormActions({
    initialActiveStep: templateFormStoreState.activeStep,
    maxStep,
  });

  // Hide stepper on edit mode
  const editModeStepStatus = useMemo<StepStatus>(() => {
    const allActiveSteps: StepStatus = {
      1: 'active',
      2: 'active',
      3: 'active',
    };
    if (canShowLocationStep) allActiveSteps[4] = 'active';
    return allActiveSteps;
  }, [canShowLocationStep]);

  const stepsValidationData = useMemo<StepsValidationData>(
    () =>
      [
        {
          isPopulated: !!nameFieldProps.value,
          hasError: !nameFieldProps.value && nameFieldProps.touched,
        },
        canShowLocationStep
          ? {
              isPopulated: !!locationsFieldProps.value.length,
              hasError: !locationsFieldProps.value.length && locationsFieldProps.touched,
            }
          : null,
        {
          isPopulated: !!typeFieldProps.value,
          hasError: !typeFieldProps.value && typeFieldProps.touched,
        },
        {
          isPopulated: !!messageFieldProps.value,
          hasError: !!(messageFieldProps.touched && (!messageFieldProps.value || isMissingRequiredTag)),
        },
      ].filter((item) => !!item),
    [
      nameFieldProps.value,
      nameFieldProps.touched,
      canShowLocationStep,
      locationsFieldProps.value,
      locationsFieldProps.touched,
      typeFieldProps.value,
      typeFieldProps.touched,
      messageFieldProps.value,
      messageFieldProps.touched,
      isMissingRequiredTag,
    ]
  );

  const reviewLandingPageEditorFeatureFlag = getFeatureFlagValue('nwx:reviews-landing-page-editor', 'ANY');

  const isFormComplete = useMemo(() => {
    const areStepsValid = Object.values(stepsValidationData).every(
      ({ isPopulated, hasError }) => isPopulated && !hasError
    );

    if (!areStepsValid) return false;

    if (!reviewLandingPageEditorFeatureFlag || !isTemplateTypeReview) {
      return true;
    }

    return quickTemplateFormVariant ? !!templateFormValues.reviewLink : !!reviewLandingPageIdFromStore;
  }, [
    stepsValidationData,
    reviewLandingPageEditorFeatureFlag,
    isTemplateTypeReview,
    quickTemplateFormVariant,
    templateFormValues.reviewLink,
    reviewLandingPageIdFromStore,
  ]);

  useEffect(() => {
    if (!isFormModeEdit) {
      updateAllStepperStatus(stepsValidationData);
      const templateString = messageFieldProps.value;
      if (initialTemplate !== templateString) {
        setInitialTemplate(templateString);
      }
    }
  }, [activeStep]);

  useEffect(() => {
    // Remove any tags that are no longer available from the template string
    const currentTemplate = (messageFieldProps.value || templateFormStoreState.templateString) ?? '';
    const tagsInTemplate = currentTemplate.match(/{{[^{}]+}}/gm);
    const tagsNoLongerAvailable = tagsInTemplate?.filter((value) => !mappedTags.some((tag) => tag.key === value));
    const isInitialLoad =
      isLoadingTemplateTypes ||
      (templateFormStoreState.templateString === currentTemplate &&
        templateFormStoreState.templateType === typeFieldProps.value);

    if (tagsNoLongerAvailable?.length && !isInitialLoad) {
      const newTemplate = tagsNoLongerAvailable.reduce<string>(
        (acc, curr) => acc.replace(new RegExp(String.raw`${curr}`, 'gm'), ''),
        currentTemplate
      );
      const newTemplateString =
        newTemplate.at(-1) === '}' && newTemplate.at(-2) === '}' ? newTemplate + ' ' : newTemplate;

      setInitialTemplate(newTemplateString);
      if (!isFormModeEdit) {
        handleMessageChange(newTemplateString); // to get the latest templateString while switching between editor and preview fields
        updateStepperStatusByStepNumber({
          step: maxStep,
          status: !!requiredTags.length || !newTemplate ? 'inactive' : 'completed',
        }); // reset message field on template type change
      }

      alert.info(
        t(
          'Some dynamic fields have been removed from the template, since they are no longer available for this template type.'
        )
      );
      return;
    }

    // reset message field on template type change
    if (!isInitialLoad && !isFormModeEdit) {
      updateStepperStatusByStepNumber({
        step: maxStep,
        status: !!requiredTags.length || !currentTemplate ? 'inactive' : 'completed',
      });
    }

    setInitialTemplate(currentTemplate);
  }, [mappedTags.map((tag) => tag.key).join('')]);

  // To save local form edits before navigating to review-landing-page.
  const saveToTemplateFormStore = useCallback(() => {
    setTemplateFormStateInStore({
      activeStep,
      templateId: templateFormStoreState.templateId,
      name: templateFormValues.name,
      locations: templateFormValues.locationIds,
      templateType: templateFormValues.type as TemplateType_Slug,
      templateString: templateFormValues.message,
    });
  }, [setTemplateFormStateInStore, templateFormValues, templateFormStoreState.templateId]);

  const isSaveTemplateLoading =
    createTemplateMutation.isLoading ||
    updateTemplateMutation.isLoading ||
    createReviewInvitationTemplate.isLoading ||
    updateReviewInvitationTemplate.isLoading;

  if (!locationIdsWithACL.length && !!selectedLocationIds.length) {
    return (
      <div
        css={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        <NoAccessEmptyState message={t('You do not have permission to manage Tag settings')} />
      </div>
    );
  }

  if (quickTemplateFormVariant) {
    return (
      <form
        {...formProps}
        css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2), padding: theme.spacing(1, 4) }}
      >
        <TemplateTypeNonStepper availableTemplateTypes={availableTemplateTypes} fieldProps={typeFieldProps} />
        <TemplateTitleNonStepper fieldProps={nameFieldProps} next={next} isFormModeEdit={isFormModeEdit} />
        {isTemplateTypeReview && (
          <ReviewLinkStep fieldProps={reviewLinkFieldProps} templateLocations={locationsFieldProps.value} />
        )}
        <MessageTemplate
          draggableTags
          hasDynamicTags
          initialTemplate={initialTemplate}
          isNewTag
          onChange={handleMessageChange}
          tags={mappedTags || []}
        >
          <MessageTemplate.Editor placeholder={t('Customize your message')} showEmojiPicker />
          <MessageTemplate.TagList showTagsOfType='REQUIRED' helperText={helperTexts.requiredTagMessage} />
          <MessageTemplate.TagList showTagsOfType='OPTIONAL' helperText={helperTexts.dynamicTagMessage} />
          <MessageTemplate.Preview css={{ marginTop: theme.spacing(1), backgroundColor: theme.colors.primary5 }} />
        </MessageTemplate>
        <Modal.StickyFooter css={{ padding: theme.spacing(4), justifyContent: 'end' }}>
          <div css={{ display: 'flex', gap: theme.spacing(1.25) }}>
            <Button
              onClick={() => {
                resetTemplateFormStore();
                formCompletionHandler?.();
              }}
              variant='secondary'
              trackingId={`${TemplatesPrefixes.Editor}-cancel-button`}
              disabled={isSaveTemplateLoading}
            >
              {t('Cancel')}
            </Button>
            <Button
              type='submit'
              variant='primary'
              disabled={!isFormComplete || isMissingRequiredTag}
              trackingId={`${TemplatesPrefixes.Editor}-save-button`}
              {...(isMissingRequiredTag ? { ...triggerProps } : {})}
            >
              {isSaveTemplateLoading ? <SpinningLoader size='xs' color='white' /> : t('Save & Continue')}
            </Button>
          </div>
          <Tooltip {...tooltipProps} css={{ textAlign: 'center' }}>
            {t('Fill all fields and add required dynamic fields in template message to save template.')}
          </Tooltip>
        </Modal.StickyFooter>
      </form>
    );
  }

  return (
    <>
      <form {...formProps} css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2), maxWidth: 700 }}>
        <Stepper
          hideStepTitle={isFormModeEdit}
          stepStatus={isFormModeEdit ? editModeStepStatus : stepStatus}
          css={{ maxWidth: 'initial', marginRight: 0 }}
        >
          <Stepper.Card
            key={'template-title'}
            css={stepperCardStyles(!!nameFieldProps.value, isFormModeEdit)}
            onClick={(step) => {
              if (!!nameFieldProps.value) skipTo(step);
            }}
            preventDefaultOnClick
            stepValue={<TemplateTileStepCollapsed fieldProps={nameFieldProps} />}
            skipAnimation={isFormModeEdit}
          >
            <TemplateTileStep fieldProps={nameFieldProps} next={next} isFormModeEdit={isFormModeEdit} />
          </Stepper.Card>

          {canShowLocationStep && (
            <Stepper.Card
              key={'template-location'}
              css={stepperCardStyles(!!locationsFieldProps.value.length, isFormModeEdit)}
              onClick={(step) => {
                if (!!locationsFieldProps.value.length) skipTo(step);
              }}
              preventDefaultOnClick
              stepValue={<LocationStepCollapsed fieldProps={locationsFieldProps} />}
              skipAnimation={isFormModeEdit}
            >
              <LocationStep
                fieldProps={locationsFieldProps}
                next={next}
                previous={previous}
                isFormModeEdit={isFormModeEdit}
                isTemplateTypeReview={isTemplateTypeReview}
                selectableLocationIds={locationIdsWithACL}
              />
            </Stepper.Card>
          )}

          <Stepper.Card
            key={'template-type'}
            css={stepperCardStyles(!!typeFieldProps.value, isFormModeEdit)}
            onClick={(step) => {
              if (!!typeFieldProps.value) skipTo(step);
            }}
            preventDefaultOnClick
            stepValue={<TemplateTypeStepCollapsed fieldProps={typeFieldProps} />}
            skipAnimation={isFormModeEdit}
          >
            <TemplateTypeStep
              availableTemplateTypes={availableTemplateTypes}
              fieldProps={typeFieldProps}
              next={next}
              previous={previous}
              isFormModeEdit={isFormModeEdit}
            />
          </Stepper.Card>

          <Stepper.Card
            key={'template-message'}
            css={stepperCardStyles(!!messageFieldProps.value && !isMissingRequiredTag, isFormModeEdit)}
            onClick={(step) => {
              if (!!messageFieldProps.value && !isMissingRequiredTag) skipTo(step);
            }}
            preventDefaultOnClick
            stepValue={
              <TemplateMessageStepCollapsed
                initialTemplate={initialTemplate}
                mappedTags={mappedTags}
                templateString={messageFieldProps.value}
              />
            }
            skipAnimation={isFormModeEdit}
          >
            <TemplateMessageStep
              initialTemplate={initialTemplate}
              mappedTags={mappedTags}
              helperTexts={helperTexts}
              review={finish}
              disableReview={!isFormComplete}
              previous={previous}
              handleMessageChange={handleMessageChange}
              isFormModeEdit={isFormModeEdit}
              isTemplateTypeReview={isTemplateTypeReview}
              saveToTemplateFormStore={saveToTemplateFormStore}
              templateLocations={locationsFieldProps.value}
            />
          </Stepper.Card>
        </Stepper>
        <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
          <Button
            type='submit'
            size='large'
            variant='primary'
            disabled={!isFormComplete || isMissingRequiredTag}
            trackingId={`${TemplatesPrefixes.Editor}-save-button`}
            {...(isMissingRequiredTag ? { ...triggerProps } : {})}
          >
            {t('Save')}
          </Button>
          <Tooltip {...tooltipProps} css={{ textAlign: 'center' }}>
            {t('Fill all fields and add required dynamic fields in template message to save template.')}
          </Tooltip>

          <Button
            onClick={() => {
              if (closeSettingsOnCancel) {
                closeSettings();
                return;
              }
              resetTemplateFormStore();
              navigate({ to: '/messaging/templates' });
            }}
            size='large'
            variant='secondary'
            trackingId={`${TemplatesPrefixes.Editor}-cancel-button`}
            disabled={isSaveTemplateLoading}
          >
            {t('Cancel')}
          </Button>
          {isSaveTemplateLoading && <SpinningLoader size='medium' />}
        </div>
      </form>
    </>
  );
};

const stepperCardStyles = (isStepCompleted: boolean, isFormModeEdit: boolean): Interpolation<Theme> => ({
  background: theme.colors.white,
  padding: theme.spacing(3),
  borderRadius: theme.borderRadius.medium,
  ...(isFormModeEdit && { overflow: 'initial' }),

  '&[aria-current="false"]': {
    ...(isStepCompleted && {
      backgroundColor: theme.colors.neutral10,
      cursor: 'pointer',
    }),
    height: 'fit-content !important', // This is needed since the height is a style prop
    '.step-content-title': {
      overflow: 'initial',
    },
  },

  '.step-content-title': {
    height: 'fit-content',
  },
});
